/**
 * Catchball — one surface when you hold the ball.
 *
 * Primary holder actions (distinct):
 *   Configure track · Configure session · Run session · Send with results
 * Plus Pass / Fork via BallPositionPanel. No Exchange L2 maze.
 *
 * Route: /prioritization/cascade/catchball/:cycleId/:loopId?ballId=
 * Canon note: docs/9-exploration/CATCHBALL-BALL-DESK.md
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  GitBranch,
  Inbox,
  Layers,
  Loader2,
  Play,
  Send,
  Settings2,
} from 'lucide-react';
import { BallPositionPanel } from '../../../components/prioritization/catchball/BallPositionPanel';
import { CatchballExchangeTimeline } from '../../../components/prioritization/catchball/CatchballExchangeTimeline';
import { CatchballStickyHeader } from '../../../components/prioritization/catchball/CatchballStickyHeader';
import { EvaluatorIntentPanel } from '../../../components/prioritization/catchball/EvaluatorIntentPanel';
import { LoopDiffBlock } from '../../../components/prioritization/catchball/LoopDiffBlock';
import { InboxPassUpResultsCard } from '../../../components/prioritization/catchball/InboxPassUpResultsCard';
import { CatchballUndoAction } from '../../../components/prioritization/catchball/CatchballUndoAction';
import { recallChildBalls } from '../../../lib/reversals/reversalsService';
import { InvolvementResponseCard } from '../../../components/prioritization/catchball/handoff/InvolvementResponseCard';
import { OpenQuestionsSection } from '../../../components/prioritization/catchball/handoff/OpenQuestionsSection';
import { RevisionRequestsSection } from '../../../components/prioritization/catchball/handoff/RevisionRequestsSection';
import { WaitingOnYouSection } from '../../../components/prioritization/catchball/handoff/WaitingOnYouSection';
import { useLoopThreads } from '../../../components/prioritization/catchball/handoff/useLoopThreads';
import {
  inquiriesAwaitingViewer,
  involvementsForViewer,
  type LoopPartyFacts,
} from '../../../components/prioritization/catchball/handoff/threadLogic';
import { SendThisUpCard } from '../../../components/prioritization/catchball/SendThisUpCard';
import { TrackResultPanel } from '../../../components/prioritization/tracks/TrackResultPanel';
import { WorkbenchSessionEmbed } from '../../../components/prioritization/catchball/WorkbenchSessionEmbed';
import { useToast } from '../../../components/ui/Toast';
import { useActorDisplayMap } from '../../../hooks/prioritization/useActorDisplayMap';
import { useOrgAwareCatchball } from '../../../hooks/prioritization/useOrgAwareCatchball';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import {
  levelConfigAssigneeUserIds,
  resolveBallHolderLabel,
  resolveRequestFromLabel,
} from '../../../lib/catchballHolderLabels';
import {
  readDesignerPathParents,
  readDesignerPathSpine,
  resolveActingOrgUnitId,
  spineStopsAboveHolder,
} from '../../../lib/catchballPathSpine';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useCatchballBallStore, useBallsForLoop } from '../../../stores/prioritization/catchballBallStore';
import { useCatchballTierTurnStore } from '../../../stores/prioritization/catchballTierTurnStore';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useCatchballStore } from '../../../stores/prioritization/catchballStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useSessionStore, useCycleSessions } from '../../../stores/prioritization/sessionStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import {
  BALL_STATUS_LABELS,
  ballChildIsBlocking,
  ballWaitingState,
  type CatchballBall,
} from '../../../types/prioritization/catchballBall';
import { normalizeLoopState } from '../../../types/prioritization/catchballLoop';
import type { CatchballTierInvolvement } from '../../../types/prioritization/catchballTierTurn';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import type { Track } from '../../../types/prioritization/track';
import { formatCalendarDate } from '../../../lib/calendarDate';
import { SESSION_STATE_LABELS } from '@/lib/sessions/vocabulary';

const EMPTY_SESSIONS: PrioritizationSession[] = [];
const EMPTY_TRACKS: Track[] = [];
const EMPTY_CHILD_BALLS: CatchballBall[] = [];
const EMPTY_INVOLVEMENTS: CatchballTierInvolvement[] = [];
const EMPTY_LOOP_IDS: string[] = [];

const SESSION_STATE_BADGE: Record<string, { label: string; class: string }> = {
  DRAFT: { label: SESSION_STATE_LABELS.DRAFT, class: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
  SETUP: { label: 'Setup', class: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  CRITERIA_FINALIZED: { label: SESSION_STATE_LABELS.CRITERIA_FINALIZED, class: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  IN_PROGRESS: { label: 'In progress', class: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  RUN_COMPLETE: { label: SESSION_STATE_LABELS.RUN_COMPLETE, class: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
  PUBLISHED: { label: SESSION_STATE_LABELS.PUBLISHED, class: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
};

function StatusStrip({
  iAmHolder,
  canClaimBall,
  ballAwaitingTake,
  holderName,
  holderStopLabel,
  directionLabel,
  dueLabel,
  cycleName,
  trackName,
  claimBusy,
  onTakeBall,
}: {
  iAmHolder: boolean;
  canClaimBall: boolean;
  /** Passer (or other non-taker): ball has moved and awaits Take at the next stop. */
  ballAwaitingTake: boolean;
  holderName: string;
  /** Org stop where the ball sits (e.g. Cloud Product Leads). */
  holderStopLabel: string | null;
  directionLabel: string | null;
  dueLabel: string | null;
  cycleName: string;
  trackName: string | null;
  claimBusy?: boolean;
  onTakeBall?: () => void;
}) {
  const stopSuffix = holderStopLabel ? ` · ${holderStopLabel}` : '';
  const personNamed =
    Boolean(holderName?.trim()) &&
    holderName.trim() !== (holderStopLabel ?? '').trim() &&
    holderName.trim() !== 'the current holder';
  const holdLabel = iAmHolder
    ? `You hold the ball${stopSuffix}`
    : canClaimBall
      ? holderStopLabel
        ? `Ball at ${holderStopLabel} — take it to continue`
        : 'Ball ready to take'
      : ballAwaitingTake && holderStopLabel
        ? personNamed
          ? `Waiting for ${holderName} to take the ball at ${holderStopLabel}`
          : `In flight to ${holderStopLabel} — waiting for them to take it`
        : personNamed
          ? `${holderName} holds the ball${stopSuffix}`
          : holderStopLabel
            ? `Ball at ${holderStopLabel} — waiting for the holder to take it`
            : `${holderName} holds the ball`;

  const takeReady = Boolean(canClaimBall && onTakeBall);

  return (
    <div
      className={`flex flex-wrap items-center gap-x-3 gap-y-1  border border-[var(--slate)] dark:border-[var(--slate)]/40 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 px-4 py-3 text-sm ${
        takeReady
          ? 'cursor-pointer hover:bg-[var(--slate-dim)]/80 dark:hover:bg-[var(--slate)]/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500'
          : ''
      } ${claimBusy ? 'opacity-60 pointer-events-none' : ''}`}
      role={takeReady ? 'button' : undefined}
      tabIndex={takeReady ? 0 : undefined}
      aria-busy={claimBusy || undefined}
      onClick={takeReady ? () => onTakeBall?.() : undefined}
      onKeyDown={
        takeReady
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onTakeBall?.();
              }
            }
          : undefined
      }
    >
      <span className="font-semibold text-[var(--text-sec)]">{holdLabel}</span>
      {directionLabel && (
        <span className="text-[var(--text-sec)]">· {directionLabel}</span>
      )}
      {dueLabel && (
        <span className="inline-flex items-center gap-1 text-[var(--text-sec)]">
          <Clock className="h-3.5 w-3.5" />
          Due {dueLabel}
        </span>
      )}
      <span className="ml-auto text-xs text-[var(--text-sec)] truncate max-w-full">
        {cycleName}
        {trackName ? ` · ${trackName}` : ''}
      </span>
    </div>
  );
}

function BranchStatusSection({
  branchBalls,
  parentBall,
  currentUserId,
  onRecalled,
}: {
  branchBalls: CatchballBall[];
  parentBall: CatchballBall | null;
  currentUserId?: string | null;
  onRecalled?: () => void;
}) {
  if (branchBalls.length === 0) return null;

  const blocking = branchBalls.filter((c) => ballChildIsBlocking(c));
  const returned = branchBalls.filter((c) =>
    ['proposal_returned', 'under_review', 'accepted', 'accepted_provisional', 'reconciled'].includes(
      c.ballStatus,
    ),
  );

  // "I am the sender/splitter, every branch untouched" (canon 27 §6 rule 2 /
  // migration 738 recall_child_balls) — the server still enforces the rest
  // (no branch opened, split further, proposed, passed on, or convened a
  // session; nobody has claimed the loop's ball) and a refusal surfaces in
  // the modal rather than being hidden.
  const canRecallFanOut =
    !!parentBall &&
    !!currentUserId &&
    (parentBall.sentByUserId === currentUserId || parentBall.createdBy === currentUserId) &&
    branchBalls.every((c) => c.ballStatus === 'sent' && !c.openedAt);

  return (
    <section className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-5 py-4 space-y-3">
      <div className="flex items-center gap-2">
        <GitBranch className="h-4 w-4 text-[var(--text-ter)]" />
        <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          Branch status
        </h2>
        <span className="text-xs text-[var(--text-ter)]">
          {returned.length}/{branchBalls.length} returned
          {blocking.length > 0 ? ` · ${blocking.length} still blocking` : ''}
        </span>
        {canRecallFanOut && parentBall && (
          <span className="ml-auto">
            <CatchballUndoAction
              triggerLabel="Recall fan-out"
              modalTitle="Recall this fan-out"
              description={`All ${branchBalls.length} branch ball${branchBalls.length === 1 ? '' : 's'} are still untouched — recalling removes them and returns this ball to before the split.`}
              confirmLabel="Recall fan-out"
              reasonPlaceholder="Why recall this fan-out? (required)"
              onConfirm={(reason) => recallChildBalls(parentBall.id, reason)}
              onSuccess={onRecalled}
              successTitle="Fan-out recalled"
              successMessage="The branch balls are gone; this ball is back to before the split."
            />
          </span>
        )}
      </div>
      {parentBall && (
        <p className="text-xs text-[var(--text-ter)]">
          Parent ball: {BALL_STATUS_LABELS[parentBall.ballStatus] ?? parentBall.ballStatus}
        </p>
      )}
      <ul className="space-y-2">
        {branchBalls.map((child) => {
          const blocking = ballChildIsBlocking(child);
          // Sent · With them · Overdue (migration 880) while it is with them;
          // null once it has come back up or is not sent yet.
          const away = ballWaitingState(child);
          const badgeClass = away?.state === 'overdue'
            ? 'bg-[var(--coral-dim)] text-[var(--coral)]'
            : blocking
              ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
              : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
          return (
            <li
              key={child.id}
              data-testid="branch-ball-row"
              className="flex items-center justify-between gap-3 text-sm border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2"
            >
              <span className="min-w-0 truncate text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                {child.requestText?.slice(0, 80) || `Branch · depth ${child.depth}`}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                {away?.holder && (
                  <span className="text-xs text-[var(--text-ter)]" data-testid="branch-ball-holder">
                    {away.holder}
                  </span>
                )}
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${badgeClass}`}
                  data-testid="branch-ball-waiting"
                >
                  {away ? away.label : BALL_STATUS_LABELS[child.ballStatus] ?? child.ballStatus}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function InboxSessionsSection({
  sessions,
  trackId,
  loopId,
  cycleId,
  ballId,
  linkedSessionIds,
  includeSessionIds,
  onToggleInclude,
  onAttachSession,
  onDetachSession,
  usedSessionProposals,
  attachingSessionId,
  /** When true, mid-tier Pass up owns send — hide C-mode SendThisUpCard. */
  suppressProposalSend,
}: {
  sessions: PrioritizationSession[];
  trackId: string;
  loopId: string;
  cycleId: string;
  ballId?: string | null;
  linkedSessionIds: Set<string>;
  includeSessionIds: Set<string>;
  onToggleInclude: (sessionId: string) => void;
  onAttachSession?: (sessionId: string) => void;
  onDetachSession?: (sessionId: string) => void;
  /** sessionId → proposal id, for sittings that already went up. */
  usedSessionProposals: Map<string, string>;
  attachingSessionId?: string | null;
  suppressProposalSend?: boolean;
}) {
  const [embeddedSessionId, setEmbeddedSessionId] = useState<string | null>(null);
  const navigate = useNavigate();
  const runExploreHref = (() => {
    const qs = new URLSearchParams({ trackId, cycleId, loopId });
    if (ballId) qs.set('ballId', ballId);
    return `/prioritization/sessions/new?${qs}`;
  })();

  return (
    <section className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-5 py-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Play className="h-4 w-4 text-[var(--green)]" />
          <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Sessions on this track
          </h2>
        </div>
        <Link
          to={`/prioritization/tracks/${trackId}?tab=sessions&loopId=${loopId}`}
          className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
        >
          + Add session
        </Link>
      </div>
      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Check <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">Include in next send</span> on a
        completed session, then use Pass results up.
      </p>

      {/* F6.1: designing the session is the work at this stop, so the action is
          present whether or not sessions already exist — previously it lived
          only in the empty state, leaving a team with one session no in-product
          path to the Event Designer. Targets /sessions/new (always the
          Designer), bound to this loop's cycle + track. */}
      <button
        type="button"
        onClick={() => navigate(runExploreHref)}
        className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 bg-[var(--green)] text-white hover:bg-[var(--green)]"
      >
        <Play className="h-3.5 w-3.5" />
        Design the session (S1–S5)
      </button>

      {sessions.length === 0 ? (
        <p className="py-2 text-sm text-[var(--text-ter)]">
          No sessions yet — design one above, then package its results into the ball.
        </p>
      ) : (
        <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {sessions.map((session) => {
            const badge =
              SESSION_STATE_BADGE[session.state] ?? {
                label: session.state,
                class: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
              };
            const complete =
              session.state === 'RUN_COMPLETE' || session.state === 'PUBLISHED';
            const expanded = embeddedSessionId === session.id;
            const include = includeSessionIds.has(session.id);
            const linked = linkedSessionIds.has(session.id);
            const usedInProposalId = usedSessionProposals.get(session.id);

            return (
              <li key={session.id} className="py-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-3">
                    <span
                      className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        complete ? 'bg-[var(--green)]' : 'bg-[var(--rule-bright)]'
                      }`}
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{session.name}</p>
                      <p className="text-xs text-[var(--text-ter)] flex flex-wrap items-center gap-1.5">
                        <span>{badge.label}</span>
                        {linked ? (
                          <span className="rounded bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                            On this ball
                          </span>
                        ) : complete ? (
                          <span className="rounded bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                            Not attached
                          </span>
                        ) : null}
                        {usedInProposalId && (
                          <Link
                            to={`/prioritization/cascade/proposal/${usedInProposalId}`}
                            className="rounded bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide hover:underline"
                          >
                            Sent up
                          </Link>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {complete && !linked && onAttachSession && (
                      <button
                        type="button"
                        disabled={attachingSessionId === session.id}
                        onClick={() => onAttachSession(session.id)}
                        className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] px-2 py-1 disabled:opacity-50"
                      >
                        {attachingSessionId === session.id ? 'Attaching…' : 'Attach to ball'}
                      </button>
                    )}
                    {complete && (
                      <label className="inline-flex items-center gap-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={include}
                          disabled={attachingSessionId === session.id}
                          onChange={() => onToggleInclude(session.id)}
                          className="rounded border-[var(--rule)]"
                        />
                        Include in next send
                      </label>
                    )}
                    {linked && onDetachSession && (
                      <button
                        type="button"
                        disabled={attachingSessionId === session.id}
                        onClick={() => onDetachSession(session.id)}
                        title="Take this session off the ball"
                        className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--coral)] px-2 py-1 disabled:opacity-50"
                      >
                        Remove
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setEmbeddedSessionId(expanded ? null : session.id)}
                      className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] px-2 py-1"
                    >
                      {expanded ? 'Close' : complete ? 'Results' : 'Work'}
                    </button>
                  </div>
                </div>
                {expanded && (
                  <div className="mt-1 space-y-3">
                    <WorkbenchSessionEmbed
                      sessionId={session.id}
                      cycleId={cycleId}
                      trackId={trackId}
                      loopId={loopId}
                      onSessionComplete={() => setEmbeddedSessionId(null)}
                    />
                    {complete && include && !suppressProposalSend && (
                      <SendThisUpCard session={session} rankedItemCount={1} />
                    )}
                  </div>
                )}
                {complete && !expanded && include && !suppressProposalSend && (
                  <SendThisUpCard session={session} rankedItemCount={1} />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function CatchballInboxPage({
  embedded = false,
  cycleIdProp,
  loopIdProp,
  ballIdProp,
}: {
  embedded?: boolean;
  cycleIdProp?: string;
  loopIdProp?: string;
  ballIdProp?: string;
} = {}) {
  const params = useParams<{ cycleId: string; loopId: string }>();
  const [searchParams] = useSearchParams();
  const cycleId = cycleIdProp ?? params.cycleId ?? '';
  const loopId = loopIdProp ?? params.loopId ?? '';
  const ballId = ballIdProp ?? searchParams.get('ballId');

  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const can = useUserStore((s) => s.can);
  const { users: systemUsers } = useSystemUsers();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const subscribeToCycleLoops = useCatchballLoopStore((s) => s.subscribeToCycleLoops);
  const claimBall = useCatchballLoopStore((s) => s.claimBall);
  const releaseBall = useCatchballLoopStore((s) => s.releaseBall);
  const loop = useCatchballLoopStore((s) => (loopId ? s.loops.find((l) => l.id === loopId) ?? null : null));
  const allLoops = useCatchballLoopStore((s) => s.loops);

  const fetchTracks = useTrackStore((s) => s.fetchTracksForCycle);
  // Select the stable `tracks` array — never call getTracksForCycle inside the
  // selector (it returns a fresh filtered array every time → infinite re-render).
  const allTracks = useTrackStore((s) => s.tracks) ?? EMPTY_TRACKS;
  const tracks = useMemo(
    () =>
      cycleId
        ? allTracks
            .filter((t) => t.cycleId === cycleId)
            .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
        : EMPTY_TRACKS,
    [allTracks, cycleId],
  );
  const fetchSessions = useSessionStore((s) => s.fetchFromSupabase);
  const sessions = useCycleSessions(cycleId);

  const fetchBallsForLoop = useCatchballBallStore((s) => s.fetchBallsForLoop);
  const ballsById = useCatchballBallStore((s) => s.ballsById);
  const loopBalls = useBallsForLoop(loopId);

  const loadLoopTurns = useCatchballTierTurnStore((s) => s.loadLoop);
  const subscribeToLoopTurns = useCatchballTierTurnStore((s) => s.subscribeToLoopTurns);
  const unsubscribeFromLoopTurns = useCatchballTierTurnStore((s) => s.unsubscribeFromLoopTurns);

  const fetchProposals = useCatchballStore((s) => s.fetchFromSupabase);
  const allProposals = useCatchballStore((s) => s.proposals);

  const cycle = useDegapProcessStore((s) =>
    cycleId ? s.cycles.find((c) => c.id === cycleId) : undefined,
  );

  const [historyOpen, setHistoryOpen] = useState(false);
  /** Questions / revision requests render only when they hold something, or when asked for. */
  const [sideChannelsOpen, setSideChannelsOpen] = useState(false);
  const [claimBusy, setClaimBusy] = useState(false);
  const [attachingSessionId, setAttachingSessionId] = useState<string | null>(null);
  const setSessionLink = useCatchballLoopStore((s) => s.setSessionLink);

  useEffect(() => {
    if (!cycleId) return;
    void fetchLoops(cycleId);
    // fetchFromSupabase already subscribes; keep an explicit subscribe so
    // possession changes from other inboxes land without a hard refresh.
    subscribeToCycleLoops(cycleId);
    void fetchTracks(cycleId);
    void fetchSessions();
    void fetchProposals();
  }, [cycleId, fetchLoops, subscribeToCycleLoops, fetchTracks, fetchSessions, fetchProposals]);

  useEffect(() => {
    if (!loopId) return;
    void fetchBallsForLoop(loopId);
    void loadLoopTurns(loopId);
    subscribeToLoopTurns(loopId);
    return () => {
      unsubscribeFromLoopTurns();
    };
  }, [
    loopId,
    fetchBallsForLoop,
    loadLoopTurns,
    subscribeToLoopTurns,
    unsubscribeFromLoopTurns,
  ]);

  const orgAware = useOrgAwareCatchball(loop, userId ?? null);

  // Stale custody after pass: claim stayed on the passer while the turn moved.
  // Release so the receiving stop can Take (matches release-on-pass going forward).
  // Never release when the viewer is placed at the holder stop — that is a
  // successful Take (atHolderStop can lag if primary org index ≠ path stop).
  const getUserOrgUnitIds = useEnterpriseIdentityStore((s) => s.getUserOrgUnitIds);
  useEffect(() => {
    if (!userId || !loopId || !loop) return;
    if (loop.ballHolderUserId !== userId) return;
    if (orgAware.atHolderStop) return;
    if (!orgAware.holderOrgUnitId) return;
    const placements = getUserOrgUnitIds(userId);
    if (placements.includes(orgAware.holderOrgUnitId)) return;
    void releaseBall(loopId, userId);
  }, [
    userId,
    loopId,
    loop,
    loop?.ballHolderUserId,
    orgAware.atHolderStop,
    orgAware.holderOrgUnitId,
    getUserOrgUnitIds,
    releaseBall,
  ]);

  const track = useMemo(
    () => (loop?.trackId ? tracks.find((t) => t.id === loop.trackId) : undefined),
    [loop?.trackId, tracks],
  );

  // Track sessions + linked ids + cycle orphans (completed sittings that lost
  // track_id — common after V3 live). Filtering to linked-only hid pass-up.
  const trackSessions = useMemo(() => {
    if (!loop) return EMPTY_SESSIONS;
    const linked = new Set(loop.sessionIds ?? []);
    const byTrack = loop.trackId
      ? sessions.filter((s) => s.trackId === loop.trackId)
      : [];
    const orphanOnCycle = sessions.filter(
      (s) =>
        !s.trackId &&
        s.cycleId === loop.cycleId &&
        (s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED'),
    );
    const byId = new Map<string, PrioritizationSession>();
    for (const s of [...byTrack, ...orphanOnCycle]) byId.set(s.id, s);
    for (const id of linked) {
      const s = sessions.find((x) => x.id === id);
      if (s) byId.set(s.id, s);
    }
    if (byId.size === 0) return EMPTY_SESSIONS;
    const rank = (s: PrioritizationSession) => {
      const complete = s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED' ? 0 : 1;
      const onBall = linked.has(s.id) ? 0 : 1;
      const updated = -(s.updatedAt?.getTime?.() ?? 0);
      return [complete, onBall, updated] as const;
    };
    return Array.from(byId.values()).sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      for (let i = 0; i < ra.length; i++) {
        if (ra[i] !== rb[i]) return ra[i] - rb[i];
      }
      return 0;
    });
  }, [sessions, loop]);

  // "Include in next send" is the loop's own state (included_session_ids, mig
  // 670), not this component's. It used to be local React state that nothing
  // persisted, nothing cleared after a send, and an effect re-checked on every
  // list change — so a sent session came straight back, and a deliberate
  // exclusion never survived a refresh.
  const includeSessionIds = useMemo(
    () => new Set(loop?.includedSessionIds ?? []),
    [loop?.includedSessionIds],
  );

  const focusBall = useMemo(() => {
    if (ballId) return ballsById[ballId] ?? loopBalls.find((b) => b.id === ballId) ?? null;
    return (
      loopBalls.find((b) => !['accepted', 'reconciled', 'closed'].includes(b.ballStatus))
      ?? loopBalls[0]
      ?? null
    );
  }, [ballId, ballsById, loopBalls]);

  const childBalls = useMemo(() => {
    if (!focusBall) return EMPTY_CHILD_BALLS;
    return Object.values(ballsById)
      .filter((b) => b.parentBallId === focusBall.id)
      .sort((a, b) => a.sequenceInLoop - b.sequenceInLoop);
  }, [focusBall, ballsById]);

  const loopProposals = useMemo(
    () => Object.values(allProposals).filter((p) => p.loopId === loopId),
    [allProposals, loopId],
  );

  const cycleOwnerOrEditor = Boolean(
    userId && ((cycle?.createdBy && cycle.createdBy === userId) || can('process.edit')),
  );

  const pathSpine = useMemo(
    () =>
      readDesignerPathSpine(
        (track?.metadata ?? null) as Record<string, unknown> | null,
      ),
    [track?.metadata],
  );

  // Prefer turns-first stop; if turns haven't hydrated yet after a pass, fall
  // back to the Path leaf so Blake at Cloud Servers still resolves for Erik.
  const holderOrgUnitIdForLabel = useMemo(() => {
    const fromTurns =
      orgAware.holderOrgUnitId ?? orgAware.activePosition?.ballHolderOrgUnitId ?? null;
    if (fromTurns) return fromTurns;
    if (pathSpine.length === 0) return null;
    const viewerStop = orgAware.userNode?.id;
    if (viewerStop) {
      const idx = pathSpine.indexOf(viewerStop);
      if (idx >= 0 && idx < pathSpine.length - 1) {
        return pathSpine[pathSpine.length - 1] ?? null;
      }
    }
    return pathSpine[pathSpine.length - 1] ?? null;
  }, [
    orgAware.holderOrgUnitId,
    orgAware.activePosition?.ballHolderOrgUnitId,
    orgAware.userNode?.id,
    pathSpine,
  ]);

  const holderActorIds = useMemo(() => {
    const ids = [
      orgAware.currentHolderUserId,
      loop?.ballHolderUserId,
      focusBall?.recipientUserId,
      ...levelConfigAssigneeUserIds(loop?.levelConfig, holderOrgUnitIdForLabel),
      loop?.requestIssuedBy,
      loop?.createdBy,
      cycle?.createdBy,
    ];
    return ids.filter((id): id is string => Boolean(id && id.trim()));
  }, [
    orgAware.currentHolderUserId,
    loop?.ballHolderUserId,
    focusBall?.recipientUserId,
    loop?.levelConfig,
    loop?.requestIssuedBy,
    loop?.createdBy,
    cycle?.createdBy,
    holderOrgUnitIdForLabel,
  ]);

  const actorDisplayNames = useActorDisplayMap(holderActorIds);

  const holderName = useMemo(
    () =>
      resolveBallHolderLabel({
        users: systemUsers,
        currentHolderUserId: orgAware.currentHolderUserId,
        loopBallHolderUserId: loop?.ballHolderUserId,
        holderOrgUnitId: holderOrgUnitIdForLabel,
        levelConfig: loop?.levelConfig,
        currentHolderLabel: orgAware.currentHolderLabel,
        displayNamesByUserId: actorDisplayNames,
      }),
    [
      orgAware.currentHolderUserId,
      orgAware.currentHolderLabel,
      loop?.ballHolderUserId,
      loop?.levelConfig,
      systemUsers,
      holderOrgUnitIdForLabel,
      actorDisplayNames,
    ],
  );

  /** Person who issued the request — never the viewer’s own tier label. */
  const requestFromLabel = useMemo(
    () =>
      resolveRequestFromLabel({
        users: systemUsers,
        requestIssuedBy: loop?.requestIssuedBy,
        loopCreatedBy: loop?.createdBy,
        cycleCreatedBy: cycle?.createdBy,
        displayNamesByUserId: actorDisplayNames,
      }),
    [
      loop?.requestIssuedBy,
      loop?.createdBy,
      cycle?.createdBy,
      systemUsers,
      actorDisplayNames,
    ],
  );

  // Mid-stop holders (Erik @ Product Leads) send up via pass_ball, not submit_proposal.
  const midTierPassUpAvailable = useMemo(() => {
    const holderId =
      orgAware.holderOrgUnitId ?? orgAware.activePosition?.ballHolderOrgUnitId ?? null;
    const parents = readDesignerPathParents(
      (track?.metadata ?? null) as Record<string, unknown> | null,
    );
    return spineStopsAboveHolder(pathSpine, holderId, parents).length > 0;
  }, [
    track?.metadata,
    pathSpine,
    orgAware.holderOrgUnitId,
    orgAware.activePosition?.ballHolderOrgUnitId,
  ]);

  const includedCompleteSessionIds = useMemo(
    () =>
      trackSessions
        .filter(
          (s) =>
            includeSessionIds.has(s.id) &&
            (s.state === 'RUN_COMPLETE' || s.state === 'PUBLISHED'),
        )
        .map((s) => s.id),
    [trackSessions, includeSessionIds],
  );
  const includedCompleteCount = includedCompleteSessionIds.length;

  const userOrgUnitIds = useEnterpriseIdentityStore((s) =>
    userId ? s.getUserOrgUnitIds(userId) : s.getUserOrgUnitIds(''),
  );

  const actingOrgUnitId = useMemo(
    () =>
      resolveActingOrgUnitId({
        userOrgUnitId: orgAware.userNode?.id,
        userOrgUnitIds,
        pathSpine,
        activeHolderOrgUnitId: orgAware.activePosition?.ballHolderOrgUnitId ?? null,
        isCycleOwnerOrEditor: cycleOwnerOrEditor,
      }),
    [
      orgAware.userNode?.id,
      userOrgUnitIds,
      orgAware.activePosition?.ballHolderOrgUnitId,
      pathSpine,
      cycleOwnerOrEditor,
    ],
  );

  // ── Inquiries / revision requests / involvements ──────────────────────────
  // The three loop-scoped conversations (canon 29): a question, a revision
  // request, and a level involvement's response. All read straight from the
  // server (no optimistic state) via useLoopThreads / catchballTierTurnStore.

  const cycleLoops = useMemo(
    () => (cycleId ? allLoops.filter((l) => l.cycleId === cycleId) : []),
    [allLoops, cycleId],
  );
  const cycleLoopIds = useMemo(
    () => (cycleLoops.length ? cycleLoops.map((l) => l.id) : EMPTY_LOOP_IDS),
    [cycleLoops],
  );

  const loopFacts: LoopPartyFacts = useMemo(
    () => ({
      ballHolderUserId: loop?.ballHolderUserId,
      createdBy: loop?.createdBy,
      requestIssuedBy: loop?.requestIssuedBy,
      isCycleOwnerOrAdmin: cycleOwnerOrEditor,
    }),
    [loop?.ballHolderUserId, loop?.createdBy, loop?.requestIssuedBy, cycleOwnerOrEditor],
  );

  // This loop's own inquiries + revision requests, for the sections below.
  const loopThreadIds = useMemo(() => (loopId ? [loopId] : EMPTY_LOOP_IDS), [loopId]);
  const loopThreads = useLoopThreads(loopThreadIds);
  const sideChannelsHaveContent =
    loopThreads.inquiries.length > 0 ||
    loopThreads.revisionRequests.length > 0 ||
    Boolean(loopThreads.error);

  // Every open loop's inquiries, for "waiting on you" — inquiries-only per
  // useLoopThreads' own guidance (revision requests aren't needed cycle-wide).
  const cycleInquiries = useLoopThreads(cycleLoopIds, { inquiriesOnly: true });

  const factsByLoop = useMemo(() => {
    const map: Record<string, LoopPartyFacts> = {};
    for (const l of cycleLoops) {
      map[l.id] = {
        ballHolderUserId: l.ballHolderUserId,
        createdBy: l.createdBy,
        requestIssuedBy: l.requestIssuedBy,
        isCycleOwnerOrAdmin: cycleOwnerOrEditor,
      };
    }
    return map;
  }, [cycleLoops, cycleOwnerOrEditor]);

  const awaitingInquiries = useMemo(
    () => inquiriesAwaitingViewer(cycleInquiries.inquiries, userId || null, factsByLoop),
    [cycleInquiries.inquiries, userId, factsByLoop],
  );
  const awaitingActorIds = useMemo(
    () => awaitingInquiries.map((q) => q.openedBy),
    [awaitingInquiries],
  );
  const awaitingActorNames = useActorDisplayMap(awaitingActorIds);
  const nameForAwaitingActor = (actorId: string) => awaitingActorNames[actorId] ?? 'A colleague';
  const labelForLoop = (lid: string) => {
    const l = cycleLoops.find((x) => x.id === lid);
    const t = l?.trackId ? tracks.find((tr) => tr.id === l.trackId) : undefined;
    return t?.name ?? (l ? `Loop ${l.loopNumber}` : 'another ball');
  };

  // Level involvements targeting an org unit the viewer belongs to on this loop.
  const loopLevelState = useCatchballTierTurnStore((s) => s.getLoopState(loopId));
  const involvements = loopLevelState?.involvements ?? EMPTY_INVOLVEMENTS;
  const orgUnitsMap = useEnterpriseIdentityStore((s) => s.orgUnits);
  const myInvolvements = useMemo(
    () => involvementsForViewer(involvements, userOrgUnitIds),
    [involvements, userOrgUnitIds],
  );

  const dueLabel = focusBall?.dueDate
    ? formatCalendarDate(focusBall.dueDate, {
        month: 'short',
        day: 'numeric',
      })
    : null;

  const directionLabel = focusBall
    ? BALL_STATUS_LABELS[focusBall.ballStatus] ?? focusBall.ballStatus
    : loop
      ? normalizeLoopState(loop.state)
      : null;

  // Configure session ≠ configure track: session setup (this sitting) vs track
  // defaults that inherit down the path. Hooks must run before early returns.
  const configureSessionHref = useMemo(() => {
    if (!track) return null;
    const setupStates = new Set(['DRAFT', 'SETUP', 'CRITERIA_FINALIZED', 'IN_PROGRESS']);
    const inSetup = trackSessions.filter((s) => setupStates.has(s.state));
    const isV3 = (s: PrioritizationSession) =>
      Boolean((s.modeConfig as { v3Config?: unknown } | undefined)?.v3Config);
    // Prefer the richest Event Designer session (item count), then any V3, then legacy.
    const ranked = [...(inSetup.length ? inSetup : trackSessions)].sort((a, b) => {
      const aV3 = isV3(a) ? 1 : 0;
      const bV3 = isV3(b) ? 1 : 0;
      if (aV3 !== bV3) return bV3 - aV3;
      const aItems =
        ((a.modeConfig as { v3Config?: { items?: unknown[] } })?.v3Config?.items?.length ?? 0) ||
        (a.itemIds?.length ?? 0);
      const bItems =
        ((b.modeConfig as { v3Config?: { items?: unknown[] } })?.v3Config?.items?.length ?? 0) ||
        (b.itemIds?.length ?? 0);
      if (aItems !== bItems) return bItems - aItems;
      return (b.updatedAt?.getTime?.() ?? 0) - (a.updatedAt?.getTime?.() ?? 0);
    });
    const target = ranked[0] ?? null;
    const qs = new URLSearchParams({
      trackId: track.id,
      cycleId,
      loopId,
    });
    if (ballId) qs.set('ballId', ballId);
    const qsStr = qs.toString();
    if (target && isV3(target)) {
      // Resume Event Designer — do NOT send V3 events to old Session Detail setup.
      return `/prioritization/sessions/new?resume=${encodeURIComponent(target.id)}&${qsStr}`;
    }
    if (target) {
      return `/prioritization/sessions/${target.id}?tab=setup&${qsStr}`;
    }
    // /sessions/new → Event Designer when Session Wizard V2 is off (Settings);
    // C-mode wizard when that module/flag is on. Prefill cycle + track + loop.
    return `/prioritization/sessions/new?${qsStr}`;
  }, [track, trackSessions, cycleId, loopId, ballId]);

  const runSessionHref = useMemo(() => {
    if (!track) return null;
    const runnable = trackSessions.find((s) =>
      ['CRITERIA_FINALIZED', 'IN_PROGRESS'].includes(s.state),
    );
    if (runnable) {
      // Keep loopId/ballId so ExchangeContextFrame shows ← Back to Catchball
      const qs = new URLSearchParams({
        loopId,
        trackId: track.id,
        cycleId,
      });
      if (ballId) qs.set('ballId', ballId);
      return `/prioritization/sessions/${runnable.id}?${qs}`;
    }
    // No runnable session yet. Returning configureSessionHref here rendered a
    // second button with a byte-identical href to "Design the session" — two
    // controls, two colours, one destination. There is nothing to run until a
    // session reaches CRITERIA_FINALIZED, so the button does not exist.
    return null;
  }, [track, trackSessions, loopId, ballId, cycleId]);

  const toggleInclude = async (sessionId: string) => {
    if (!loopId) return;
    const nextIncluded = !includeSessionIds.has(sessionId);
    setAttachingSessionId(sessionId);
    try {
      // Including implies attaching — the checkbox is the only affordance many
      // facilitators ever touch, and inclusion is meaningless off the ball.
      const ok = await setSessionLink(loopId, sessionId, { included: nextIncluded });
      if (!ok) {
        showToast(
          'error',
          nextIncluded ? 'Could not include session' : 'Could not exclude session',
          useCatchballLoopStore.getState().error ??
            'Check that you hold the ball or facilitated this session.',
        );
      }
    } finally {
      setAttachingSessionId(null);
    }
  };

  const linkedSessionIds = useMemo(
    () => new Set(loop?.sessionIds ?? []),
    [loop?.sessionIds],
  );

  // Sessions that already backed a proposal on this loop. A used sitting is not
  // offered for the next send again — nothing recorded this before mig 670 added
  // catchball_proposals.session_id, so the Inbox could not tell used from unused.
  const usedSessionProposals = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of loopProposals) {
      const sid = (p as { sessionId?: string | null }).sessionId;
      if (sid && !map.has(sid)) map.set(sid, p.id);
    }
    return map;
  }, [loopProposals]);

  const handleAttachSession = async (sessionId: string) => {
    if (!loopId) return;
    setAttachingSessionId(sessionId);
    try {
      const ok = await setSessionLink(loopId, sessionId, { attached: true, included: true });
      if (!ok) {
        showToast(
          'error',
          'Could not attach session',
          useCatchballLoopStore.getState().error ??
            'Check that you hold the ball or facilitated this session.',
        );
        return;
      }
      showToast('success', 'Session attached', 'Included for the next pass-up.');
    } finally {
      setAttachingSessionId(null);
    }
  };

  const handleDetachSession = async (sessionId: string) => {
    if (!loopId) return;
    setAttachingSessionId(sessionId);
    try {
      const ok = await setSessionLink(loopId, sessionId, { attached: false });
      if (!ok) {
        showToast(
          'error',
          'Could not remove session',
          useCatchballLoopStore.getState().error ??
            'Check that you hold the ball or facilitated this session.',
        );
        return;
      }
      showToast('success', 'Session removed from this ball', 'It no longer rides the next send.');
    } finally {
      setAttachingSessionId(null);
    }
  };

  const handleClaim = async () => {
    if (!userId || !loopId) return;
    setClaimBusy(true);
    try {
      const ok = await claimBall(loopId, userId);
      if (!ok) {
        const rpcError = useCatchballLoopStore.getState().error;
        showToast('error', 'Could not take the ball', rpcError ?? 'Failed to claim the ball');
        return;
      }
      showToast('success', 'You have the ball', 'Pass, fork, or run work from your Inbox.');
    } finally {
      setClaimBusy(false);
    }
  };

  if (!cycleId || !loopId) {
    return (
      <div className="p-8 text-center text-sm text-[var(--text-ter)]">Missing cycle or loop.</div>
    );
  }

  if (!loop) {
    return (
      <div className="flex items-center justify-center py-24 text-[var(--text-ter)]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Opening Catchball…
      </div>
    );
  }

  /**
   * One action leads. The strip used to present four equal-weight buttons
   * (Configure track / Configure session / Run session / Send with results)
   * with no indication of order, which is why the page needed a paragraph
   * explaining how sending works. The lead is derived from where the ball
   * actually is; the rest stay available, quietly.
   */
  const primaryAction: 'claim' | 'design' | 'run' | 'send' =
    orgAware.ballUnclaimed && orgAware.canClaimBall
      ? 'claim'
      : includedCompleteCount > 0 || midTierPassUpAvailable
        ? 'send'
        : runSessionHref
          ? 'run'
          : 'design';

  const leadClass =
    'inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 bg-[var(--cta)] text-[var(--cta-ink)] hover:opacity-90 disabled:opacity-50 min-h-[44px]';
  const quietClass =
    'inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] min-h-[44px]';

  const stickyActions = (
    <div className="flex flex-wrap gap-2">
      {orgAware.ballUnclaimed && orgAware.canClaimBall && (
        <button
          type="button"
          disabled={claimBusy}
          onClick={() => void handleClaim()}
          className={primaryAction === 'claim' ? leadClass : quietClass}
        >
          <Inbox className="h-4 w-4" />
          Take the ball
        </button>
      )}
      {configureSessionHref && (
        <Link to={configureSessionHref} className={primaryAction === 'design' ? leadClass : quietClass}>
          <Settings2 className="h-4 w-4" />
          Design the session
        </Link>
      )}
      {runSessionHref && (
        <Link to={runSessionHref} className={primaryAction === 'run' ? leadClass : quietClass}>
          <Play className="h-4 w-4" />
          Run session
        </Link>
      )}
      <a
        href={midTierPassUpAvailable ? '#inbox-pass-up' : '#inbox-send'}
        className={primaryAction === 'send' ? leadClass : quietClass}
      >
        <Send className="h-4 w-4" />
        {midTierPassUpAvailable ? 'Send results up' : 'Send with results'}
      </a>
      {track && (
        <Link to={`/prioritization/tracks/${track.id}?cycleId=${cycleId}`} className={quietClass}>
          <Layers className="h-4 w-4" />
          Configure track
        </Link>
      )}
      {/* Was labelled "Inbox" — the third thing on this screen wearing that word. */}
      <Link
        to={`/prioritization/cascade/inbox/${cycleId}`}
        className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2.5 text-[var(--text-ter)] hover:text-[var(--text-pri)] min-h-[44px]"
      >
        Proposal review
      </Link>
    </div>
  );

  return (
    <div className={embedded ? 'py-2' : 'min-h-full bg-[var(--surface)] dark:bg-[var(--bg)] pb-28 md:pb-10'}>
      {!embedded && (
        <CatchballStickyHeader cycleId={cycleId} active="workbench" workbenchLoopId={loopId} />
      )}

      <div className={embedded ? 'space-y-5' : 'max-w-3xl mx-auto px-4 py-6 sm:px-6 space-y-5'}>
        {!embedded && (
          <nav className="flex items-center gap-2 text-xs text-[var(--text-ter)]">
            <button
              type="button"
              onClick={() => navigate(`/prioritization/cycles/${cycleId}?tab=exchange`)}
              className="inline-flex items-center gap-1 hover:text-[var(--text-sec)]"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {cycle?.name ?? 'Cycle'}
            </button>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[var(--text-sec)]">Catchball</span>
          </nav>
        )}

        {/* The page is titled by the work, not by the mechanism. "Catchball" is
            already the breadcrumb and the tab; repeating it as the H1 told the
            holder nothing about what they were holding. */}
        <header className="space-y-1">
          <h1 className="font-fieldwork text-2xl text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {loop.intent?.asks?.[0] ?? loop.intent?.pathName ?? labelForLoop(loop.id)}
          </h1>
          <p className="text-sm text-[var(--text-ter)]">
            {[track?.name, cycle?.name].filter(Boolean).join(' · ') || 'Catchball'}
          </p>
        </header>

        <WaitingOnYouSection
          cycleId={cycleId}
          currentLoopId={loopId}
          inquiries={awaitingInquiries}
          labelForLoop={labelForLoop}
          nameForActor={nameForAwaitingActor}
        />

        {myInvolvements.map((inv) => (
          <InvolvementResponseCard
            key={inv.id}
            involvement={inv}
            viewerId={userId || null}
            holderOrgUnitName={orgUnitsMap.get(inv.holderOrgUnitId)?.name}
            targetOrgUnitName={orgUnitsMap.get(inv.targetOrgUnitId)?.name}
          />
        ))}

        <StatusStrip
          iAmHolder={orgAware.iAmHolder}
          canClaimBall={orgAware.canClaimBall}
          ballAwaitingTake={Boolean(orgAware.ballAwaitingTake)}
          holderName={holderName}
          holderStopLabel={orgAware.currentHolderLabel}
          directionLabel={directionLabel}
          dueLabel={dueLabel}
          cycleName={cycle?.name ?? 'Cycle'}
          trackName={track?.name ?? null}
          claimBusy={claimBusy}
          onTakeBall={
            orgAware.ballUnclaimed && orgAware.canClaimBall
              ? () => {
                  void handleClaim();
                }
              : undefined
          }
        />

        {/* Primary action strip — sticky on mobile */}
        <div className="hidden md:block">{stickyActions}</div>

        {/* What changed in the request since the last pass of this ball, and
            what the decider said (roadmap C-1). Hidden on a first pass. */}
        <LoopDiffBlock loopId={loop.id} variant="request" />

        {/* The request */}
        {loop.intent ? (
          <EvaluatorIntentPanel
            intent={loop.intent}
            requestFromLabel={requestFromLabel}
            tossedAt={loop.requestIssuedAt?.toISOString()}
          />
        ) : (
          <section className="border border-dashed border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-5 py-6 text-center">
            <Inbox className="mx-auto h-7 w-7 text-[var(--text-ter)] mb-2" />
            <p className="text-sm text-[var(--text-ter)]">No request on this loop yet.</p>
            <Link
              to={`/prioritization/cascade/compose/${cycleId}${track ? `?trackId=${track.id}` : ''}`}
              className="mt-3 inline-flex text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
            >
              Compose request →
            </Link>
          </section>
        )}

        {/* What you inherit */}
        <section className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                What you inherit
              </h2>
              <p className="text-xs text-[var(--text-ter)] mt-1">
                Participants, criteria, values and pool come from the track.
              </p>
            </div>
            {track ? (
              <Link
                to={`/prioritization/tracks/${track.id}?cycleId=${cycleId}`}
                className="shrink-0 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
              >
                Configure on track →
              </Link>
            ) : (
              <span className="text-xs text-[var(--mustard)]">No track linked</span>
            )}
          </div>
          {track && (
            <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              <div>
                <dt className="text-[var(--text-ter)] uppercase tracking-wide">Track</dt>
                <dd className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{track.name}</dd>
              </div>
              <div>
                <dt className="text-[var(--text-ter)] uppercase tracking-wide">Sessions</dt>
                <dd className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  {trackSessions.length}
                </dd>
              </div>
            </dl>
          )}
        </section>

        {/* The track's published result — closed sessions come back here, and the
            person who holds the ball decides what is published for the track (mig 934). */}
        {track && (
          <TrackResultPanel
            trackId={track.id}
            refreshKey={`${loop.ballHolderUserId ?? ''}:${trackSessions.length}`}
            showPageLink
          />
        )}

        {/* Sessions first — include/attach must be discoverable before Pass up. */}
        {track && (
          <div id="inbox-send">
            <InboxSessionsSection
              sessions={trackSessions}
              trackId={track.id}
              loopId={loopId}
              cycleId={cycleId}
              ballId={ballId}
              linkedSessionIds={linkedSessionIds}
              includeSessionIds={includeSessionIds}
              onToggleInclude={(id) => void toggleInclude(id)}
              onDetachSession={(id) => void handleDetachSession(id)}
              usedSessionProposals={usedSessionProposals}
              onAttachSession={(id) => void handleAttachSession(id)}
              attachingSessionId={attachingSessionId}
              suppressProposalSend={midTierPassUpAvailable}
            />
          </div>
        )}

        {/* Mid-tier send-up: pass_ball mid→mid; form+submit proposal when target is Path anchor */}
        {midTierPassUpAvailable && (orgAware.atHolderStop || orgAware.iAmHolder) && (
          <InboxPassUpResultsCard
            loopId={loopId}
            cycleId={cycleId}
            trackId={loop.trackId}
            workspaceId={cycle?.workspaceId ?? getCurrentWorkspaceId() ?? ''}
            pathSpine={pathSpine}
            holderOrgUnitId={
              orgAware.holderOrgUnitId ?? orgAware.activePosition?.ballHolderOrgUnitId
            }
            iAmHolder={orgAware.iAmHolder}
            includedSessionIds={includedCompleteSessionIds}
            includedSessionCount={includedCompleteCount}
          />
        )}

        {/* Pass / Fork — reuse BallPositionPanel (no second pass engine) */}
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] px-1">
            Pass & fork
          </h2>
          <BallPositionPanel
            loopId={loopId}
            cycleId={cycleId}
            trackId={loop.trackId}
            userOrgUnitId={orgAware.userNode?.id}
            actingOrgUnitId={actingOrgUnitId}
            recipientOrgUnitIds={loop.intent?.recipientOrgUnitIds}
            possessionHolderName={
              orgAware.ballUnclaimed
                ? null
                : orgAware.iAmHolder
                  ? 'You'
                  : holderName
            }
            possessionOrgLabel={orgAware.currentHolderLabel}
            isCycleOwnerOrEditor={cycleOwnerOrEditor}
            canActAsHolder={orgAware.iAmHolder}
          />
        </section>

        {/* Questions and revision requests: two full panels used to render on
            every ball to announce that they held nothing. They now appear when
            there is something in them, or on demand from one line. */}
        {(sideChannelsHaveContent || sideChannelsOpen) ? (
          <>
            <OpenQuestionsSection
              loopId={loopId}
              viewerId={userId || null}
              viewerName={userName}
              inquiries={loopThreads.inquiries}
              readError={loopThreads.error}
              onChanged={loopThreads.refetch}
            />

            <RevisionRequestsSection
              loopId={loopId}
              viewerId={userId || null}
              viewerName={userName}
              loopFacts={loopFacts}
              revisionRequests={loopThreads.revisionRequests}
              readError={loopThreads.error}
              onChanged={async () => {
                await loopThreads.refetch();
                // Closing/creating a revision request moves loop state (713).
                await fetchLoops(cycleId);
              }}
            />
          </>
        ) : (
          <button
            type="button"
            onClick={() => setSideChannelsOpen(true)}
            className="w-full text-left text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] px-1 py-1"
          >
            Ask a question or request a revision →
          </button>
        )}

        {/* The "How sending works" panel that stood here explained the app's own
            navigation in a paragraph ("use ← Back to Catchball in the indigo bar").
            A screen that needs prose to explain its buttons has the wrong buttons;
            the state-derived action strip at the top now says what to do next.
            Its sibling — a second panel repeating "Send with results" while the
            real control sits in the sessions list — went with it. */}
        {includedCompleteCount > 0 && !midTierPassUpAvailable && (
          <p className="flex items-center gap-2 text-xs text-[var(--text-ter)] px-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] shrink-0" />
            {includedCompleteCount} session{includedCompleteCount === 1 ? '' : 's'} packaged — send from the session above, or pass without results below.
          </p>
        )}

        <BranchStatusSection
          branchBalls={childBalls}
          parentBall={focusBall}
          currentUserId={userId}
          onRecalled={() => void fetchBallsForLoop(loopId)}
        />

        {/* History — one collapsible, not five peer tabs */}
        <section className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            className="w-full flex items-center justify-between gap-3 px-5 py-3.5 text-left hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
          >
            <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              What happened
            </span>
            {historyOpen ? (
              <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />
            ) : (
              <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
            )}
          </button>
          {historyOpen && (
            <div className="px-5 pb-5 border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4 space-y-4">
              <CatchballExchangeTimeline
                loop={loop}
                proposals={loopProposals as unknown as import('../../../types/prioritization/catchballProposal').CatchballProposal[]}
                cycleOwnerName={undefined}
              />
            </div>
          )}
        </section>
      </div>

      {/* Mobile sticky action sheet — sits above MobileShell's bottom tab bar
          (--pa-tabbar-h is scoped to .pa-mobile; outside the shell both vars
          resolve to 0px and this stays bottom-0 as before). */}
      <div className="md:hidden fixed bottom-[calc(var(--pa-tabbar-h,0px)+var(--pa-safe-bottom,0px))] inset-x-0 z-40 border-t border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)]/95 dark:bg-[var(--surface)]/95 backdrop-blur px-4 py-3 safe-area-pb">
        {stickyActions}
      </div>
    </div>
  );
}

export default CatchballInboxPage;
